// This file was previously HeroVariantMix.tsx and is now the canonical Hero component.
'use client'

import { useLanguage } from '@/contexts/LanguageContext'
import { navigationTranslations } from '@/translations/navigation'
import { useState, useEffect } from 'react'
import { Phone, Menu, X, Globe } from 'lucide-react'
import Image from 'next/image'

function OverlayNavbar() {
  const [isScrolled, setIsScrolled] = useState(false)
  const { language, setLanguage } = useLanguage()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const languages = [
    { code: 'en', name: 'English', abbr: 'GB' },
    { code: 'cy', name: 'Cymraeg', abbr: 'CY' }
  ]
  const t = navigationTranslations[language]
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  
  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sharp' : 'bg-transparent'}`}>
      <div className="container-sharp flex items-center justify-between h-16">
        <a
          href="#home"
          className="flex items-center space-x-2"
          onClick={e => {
            e.preventDefault();
            const hero = document.getElementById('home');
            if (hero) hero.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
            <Image 
              src="/images/hero/logo.webp" 
              alt="JC Gardening Logo" 
              width={32} 
              height={32}
              className="w-full h-full object-contain"
            />
          </div>
          <span className={`text-xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>JC Gardening</span>
        </a>
        <div className="hidden xl:flex items-center space-x-8">
          <a href="#about" className={`nav-link ${isScrolled ? 'text-gray-700' : 'text-white'}`}>{t.navbar.navigation.about}</a>
          <a href="#services" className={`nav-link ${isScrolled ? 'text-gray-700' : 'text-white'}`}>{t.navbar.navigation.services}</a>
          <a href="#portfolio" className={`nav-link ${isScrolled ? 'text-gray-700' : 'text-white'}`}>{t.footer.quickLinks.portfolio}</a>
          <a href="#contact" className={`nav-link ${isScrolled ? 'text-gray-700' : 'text-white'}`}>{t.navbar.navigation.contact}</a>
          <div className="flex items-center space-x-2">
            <Phone className={`w-4 h-4 ${isScrolled ? 'text-gray-900' : 'text-white'}`} />
            <a
              href="tel:+447879643396"
              className={`font-semibold transition-colors duration-200 px-2 py-1 rounded ${isScrolled ? 'text-gray-900 hover:text-gray-700' : 'text-white hover:text-gray-200'}`}
            >
              (+44) 7879643396
            </a>
          </div>
          {/* Language Switcher */}
          <div className="relative ml-4">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-medium transition-colors duration-200 ${isScrolled ? 'bg-gradient-to-br from-gray-900 to-gray-800 text-white hover:from-gray-800 hover:to-gray-900' : 'bg-white/20 text-white hover:bg-white/30'}`}
            >
              <Globe className="w-4 h-4" />
              <span>{languages.find(l => l.code === language)?.name}</span>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-36 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { setLanguage(lang.code as 'en' | 'cy'); setIsDropdownOpen(false); }}
                    className={`w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-gray-50 transition-colors ${language === lang.code ? 'text-gray-900 bg-gray-100' : 'text-gray-700'}`}
                  >
                    <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">{lang.abbr}</span>
                    <span className="font-medium">{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* Mobile Burger Menu Button */}
        <button
          className={`xl:hidden p-2 rounded-lg transition-colors ${isScrolled ? 'bg-white/95' : 'hover:bg-gray-100'}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          {isMobileMenuOpen ? (
            <X className={`w-6 h-6 ${isScrolled ? 'text-gray-900' : 'text-white'}`} />
          ) : (
            <Menu className={`w-6 h-6 ${isScrolled ? 'text-gray-900' : 'text-white'}`} />
          )}
        </button>
        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="xl:hidden absolute top-16 left-0 right-0 bg-gradient-to-br from-gray-900 to-gray-800 z-50 shadow-lg border-b border-gray-800 animate-fade-in-down">
            <div className="flex flex-col items-center py-6 space-y-4">
              <a href="#about" className="nav-link text-white text-lg" onClick={() => setIsMobileMenuOpen(false)}>{t.navbar.navigation.about}</a>
              <a href="#services" className="nav-link text-white text-lg" onClick={() => setIsMobileMenuOpen(false)}>{t.navbar.navigation.services}</a>
              <a href="#portfolio" className="nav-link text-white text-lg" onClick={() => setIsMobileMenuOpen(false)}>{t.footer.quickLinks.portfolio}</a>
              <a href="#contact" className="nav-link text-white text-lg" onClick={() => setIsMobileMenuOpen(false)}>{t.navbar.navigation.contact}</a>
              <div className="flex items-center space-x-2 mt-4">
                <Phone className="w-4 h-4 text-gray-300" />
                <a
                  href="tel:+447879643396"
                  aria-label="Call JC Gardening"
                  className="font-semibold text-gray-300 px-2 py-1 rounded"
                >
                  (+44) 7879643396
                </a>
              </div>
              {/* Mobile Language Switcher */}
              <div className="flex items-center space-x-2 mt-4">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { setLanguage(lang.code as 'en' | 'cy'); setIsMobileMenuOpen(false); }}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors duration-200 ${language === lang.code ? 'bg-gray-700 text-white' : 'bg-white/20 text-white hover:bg-white/30'}`}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}

export default function Hero() {
  const { language } = useLanguage()

  return (
    <section id="home" className="relative min-h-[100dvh] flex flex-col justify-center items-center bg-gray-900 px-8 sm:px-4 pt-24 pb-12 text-center overflow-hidden">
      <OverlayNavbar />
      
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 opacity-3">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Scattered subtle dots */}
          <circle cx="15" cy="20" r="0.3" fill="white" opacity="0.2"/>
          <circle cx="85" cy="25" r="0.2" fill="white" opacity="0.15"/>
          <circle cx="25" cy="45" r="0.4" fill="white" opacity="0.1"/>
          <circle cx="75" cy="50" r="0.3" fill="white" opacity="0.2"/>
          <circle cx="45" cy="70" r="0.2" fill="white" opacity="0.15"/>
          <circle cx="90" cy="75" r="0.3" fill="white" opacity="0.1"/>
          <circle cx="10" cy="80" r="0.2" fill="white" opacity="0.2"/>
          <circle cx="60" cy="85" r="0.3" fill="white" opacity="0.15"/>
        </svg>
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center pt-8 lg:pt-16">
        <div className="text-center">
          {/* Main Bold Text */}
          <h1 className="text-6xl md:text-8xl lg:text-9xl xl:text-[12rem] font-black leading-none text-white mb-1 md:mb-2 lg:mb-2 tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            {language === 'en' ? (
              <>
                <span className="block mb-8 md:mb-8 lg:mb-12">
                  <span className="bg-gray-200 rounded px-4 py-2 text-gray-800 font-semibold" style={{ fontFamily: 'Playfair Display, serif', fontWeight: 600, fontSize: '0.9em' }}>TRANSFORM</span>
                </span>
                <span className="block relative mb-6 md:mb-6 lg:mb-8">
                  <span className="relative border-b-4 border-white pb-2">
                    YOUR
                  </span>
                </span>
                <span className="block relative">
                  <span className="relative">
                    GARDEN
                  </span>
                </span>
              </>
            ) : (
              <>
                <span className="block mb-8 md:mb-8 lg:mb-12">
                  <span className="bg-gray-200 rounded px-4 py-2 text-gray-800 font-semibold" style={{ fontFamily: 'Playfair Display, serif', fontWeight: 600, fontSize: '0.9em' }}>TRAWSNEWID</span>
                </span>
                <span className="block relative mb-6 md:mb-6 lg:mb-8">
                  <span className="relative border-b-4 border-white pb-2">
                    EICH
                  </span>
                </span>
                <span className="block relative">
                  <span className="relative">
                    GARDD
                  </span>
                </span>
              </>
            )}
          </h1>

          {/* Single line subtext */}
          <p className="text-base md:text-xl text-gray-400 max-w-5xl mx-auto font-light tracking-wide px-4">
            {language === 'en' 
              ? "Professional garden care and maintenance. I'll help you discover its potential."
              : "Gofal a chynnal a chadw gardd proffesiynol. Byddaf yn eich helpu i ddarganfod ei botensial."
            }
          </p>
        </div>
      </div>
    </section>
  )
}